import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
} from '@material-ui/core';
import keys from 'lodash/keys';
import map from 'lodash/map';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import encryptor from '../../../lib/utils/encryptor';
import {
  getStoredConnections,
  removeConnection,
  saveApiConnection,
  saveCognitoConnection,
  saveLambdaConnection,
  saveOidcConnection,
  saveOktaConnection,
} from '../../../store/utils/authStorage';
import EditConnectionDialog from './editConnection';

const ExistingConnectionDialog = ({ open, onClose, connect }) => {
  const isConnecting = useSelector(state => state.auth.isConnecting);
  const [connections, setConnections] = useState(getStoredConnections());
  const [connection, selectConnection] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const openEditDialog = () => setEditDialogOpen(true);
  const onEditDialogClose = () => setEditDialogOpen(false);
  const onEditSave = (newConnection) => {
    removeConnection(connection);
    switch (newConnection.authType) {
      case 'cognito': saveCognitoConnection(newConnection); break;
      case 'apiKey': saveApiConnection(newConnection); break;
      case 'lambda': saveLambdaConnection(newConnection); break;
      case 'okta': saveOktaConnection(newConnection); break;
      case 'oidc': saveOidcConnection(newConnection); break;
      default: break;
    }
    setConnections(getStoredConnections());
    setEditDialogOpen(false);
    selectConnection(newConnection.name);
  };
  const onEditDelete = () => {
    removeConnection(connection);
    setConnections(getStoredConnections());
    setEditDialogOpen(false);
    selectConnection('');
  };

  const onSelect = event => event && selectConnection(event.target.value);

  const onSubmit = () => {
    const auth = connections[connection];
    if (auth.authType === 'apiKey' || auth.apiKey) {
      connect('apiKey', {
        name: connection,
        graphqlEndpoint: encryptor.decrypt(auth.graphqlEndpoint),
        apiKey: encryptor.decrypt(auth.apiKey),
      });
    }
    else if(auth.authType === 'lambda') {
      connect('lambda', {
        name: connection,
        graphqlEndpoint: encryptor.decrypt(auth.graphqlEndpoint),
        authorizationToken: encryptor.decrypt(auth.authorizationToken),
        authType: 'lambda',
      });
    }
    else if (auth.authType === 'okta') {
      connect('okta', {
        name: connection,
        graphqlEndpoint: encryptor.decrypt(auth.graphqlEndpoint),
        oktaServer: encryptor.decrypt(auth.oktaServer),
        userpoolClientId: encryptor.decrypt(auth.userpoolClientId),
        authType: 'okta'
      });
    }
    else if (auth.authType === 'cognito') {
      connect('cognito', {
        name: connection,
        userpoolId: encryptor.decrypt(auth.userpoolId),
        userpoolClientId: encryptor.decrypt(auth.userpoolClientId),
        graphqlEndpoint: encryptor.decrypt(auth.graphqlEndpoint),
        username: encryptor.decrypt(auth.username),
        password: encryptor.decrypt(auth.password),
      });
    }
    else if(auth.authType === 'oidc') {
      connect('oidc', {
        name: connection,
        graphqlEndpoint: encryptor.decrypt(auth.graphqlEndpoint),
        oidcIssuer: encryptor.decrypt(auth.oidcIssuer),
        oidcClientId: encryptor.decrypt(auth.oidcClientId),
        authType: auth.authTYpe
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} aria-labelledby='existing_connection_form'>
        <DialogTitle id='existing_connection_form'>Connect to existing connections</DialogTitle>

        <DialogContent id='existing_connection_form--content'>
          <DialogContentText>Select a previously saved connection.</DialogContentText>

          <FormControl variant='outlined'>
            <Select
              labelId='demo-simple-select-outlined-label'
              id='demo-simple-select-outlined'
              onChange={onSelect}
              value={connection}
            >
              {map(keys(connections).sort(), connectionItem => (
                <MenuItem value={connectionItem} key={connectionItem}>
                  {connectionItem}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DialogActions>
            <div className="secondary-actions">
              <Button onClick={openEditDialog} color="default" disabled={!connection || isConnecting}>Edit</Button>
            </div>
            <div className="primary-actions">
              <Button onClick={onClose} color='secondary' disabled={isConnecting}>Cancel</Button>
              <Button onClick={onSubmit} color='primary' disabled={!connection || isConnecting}>
                Connect
                {isConnecting && <CircularProgress size='1rem' className='circular_progress' />}
              </Button>
            </div>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {!!connection &&
        <EditConnectionDialog
          oldConnectionKey={connection}
          open={!!editDialogOpen}
          onClose={onEditDialogClose}
          onSave={onEditSave}
          onDelete={onEditDelete}
        />
      }
    </>
  );
};

ExistingConnectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  connect: PropTypes.func.isRequired,
};

export default ExistingConnectionDialog;
